import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import GridOn from "@mui/icons-material/GridOn";
import { Box, Button, Popover, Stack, TextField } from "@mui/material";
import { type FC, useCallback, useState } from "react";
import { usePopover } from "../../../../../utils/hooks/use-popover";
import { INSERT_DATA_TABLE_COMMAND } from "../../types/commands";
import type { ToolbarProps } from "./props";
import { ToolbarIconButton } from "./toolbar-icon-button";

export const TableButton: FC<ToolbarProps & { label?: string }> = ({
  disabled,
  label,
  onClick,
}) => {
  const [editor] = useLexicalComposerContext();
  const { anchorRef, handleClose, handleOpen, open } = usePopover<HTMLButtonElement>();
  const [tableConfig, setTableConfig] = useState({
    columns: 2,
    rows: 2,
  });

  const insertTable = useCallback(() => {
    editor.dispatchCommand(INSERT_DATA_TABLE_COMMAND, {
      ...tableConfig,
      border: { width: 1, style: "solid", colour: "#000" },
      cellPadding: { setIndividually: false, all: 1 },
      backgroundColour: "red",
    });

    handleClose();
    onClick?.();
  }, [editor, onClick, handleClose, tableConfig]);

  return (
    <>
      <ToolbarIconButton
        ref={anchorRef}
        data-analytics-id="lexical-toolbar-table"
        sx={{ width: label && "100%", justifyContent: "start" }}
        disabled={disabled}
        label={label}
        icon={<GridOn fontSize="medium" />}
        data-testid="toolar-button-columns"
        aria-label="Columns"
        tabIndex={-1}
        onClick={() => {
          handleOpen();
        }}
      />
      <Popover
        disableAutoFocus={true}
        open={open}
        // eslint-disable-next-line react-hooks/refs
        anchorEl={anchorRef.current}
        onClose={() => {
          handleClose();
        }}
        sx={{
          width: "250px",
        }}
        slotProps={{
          paper: {
            sx: {
              width: "100%",
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Rows"
              type="number"
              value={tableConfig.rows}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (value && value > 0 && value <= 20) {
                  setTableConfig({ ...tableConfig, rows: value });
                }
              }}
              slotProps={{
                htmlInput: {
                  min: 1,
                  max: 20,
                },
              }}
              size="small"
              fullWidth
            />
            <TextField
              label="Columns"
              type="number"
              value={tableConfig.columns}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (value && value > 0 && value <= 20) {
                  setTableConfig({ ...tableConfig, columns: value });
                }
              }}
              slotProps={{
                htmlInput: {
                  min: 1,
                  max: 20,
                },
              }}
              size="small"
              fullWidth
            />
            <Button
              variant="contained"
              onClick={insertTable}
              data-analytics-id="lexical-toolbar-table-insert"
              fullWidth
            >
              Insert Table
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};
